import React from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  AccordComponent,
  CopyValue,
  DocumentList,
  FieldLabel,
  FieldValue,
  Line,
  SubHeading,
  yesNo,
} from './ApplicationDetailKit'
import {
  ADDRESS_HISTORY_YEARS,
  addressCoverage,
  describeGap,
  employmentGaps,
  formatAddress,
} from '../applicationHistory'

// The read-only sections of the reviewer's application page.

const DVLA_CHECK_URL = 'https://www.gov.uk/check-driving-information'

const formatMonth = (date) => (date ? moment(date).format('MMM YYYY') : null)
const formatDay = (date) => (date ? moment(date).format('D MMM YYYY') : null)

const span = (from, to) => {
  if (!from) return null
  const months = moment(to ?? undefined).diff(moment(from), 'months')
  if (months < 1) return 'under a month'
  const years = Math.floor(months / 12)
  const rest = months % 12
  return [
    years && `${years} yr${years === 1 ? '' : 's'}`,
    rest && `${rest} mo${rest === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' ')
}

const StatusChip = ({ label, color }) => (
  <Chip
    size="small"
    label={label}
    color={color}
    variant="outlined"
    sx={{ borderRadius: 1, fontWeight: 600 }}
  />
)

// ── Personal ────────────────────────────────────────────────────────────
export const PersonalSection = ({ application }) => {
  const info = application?.employee?.personnelInfo ?? {}
  const contact = info.emergencyContact ?? {}
  const dob = info.dob
  return (
    <AccordComponent title="Personal details" defaultOpen>
      <Line
        title="Name"
        value={[
          application?.userAccount?.firstName ?? application?.userInfo?.firstName,
          application?.userAccount?.lastName ?? application?.userInfo?.lastName,
        ]
          .filter(Boolean)
          .join(' ')}
      />
      <Line title="Pronouns" value={info.pronouns} />
      <Line
        title="Date of birth"
        value={
          dob &&
          `${formatDay(dob)} (age ${moment().diff(moment(dob), 'years')})`
        }
      />
      <Line title="NI number" value={info.nationalInsurance} />
      <Line title="Nationality" value={info.nationality} />
      <Line title="Birthplace" value={info.birthplace} />
      <SubHeading>Emergency contact</SubHeading>
      <Line
        title="Name"
        value={[contact.firstName, contact.lastName].filter(Boolean).join(' ')}
      />
      <Line title="Relationship" value={contact.relationship} />
      <Line title="Phone" value={contact.phone1} />
    </AccordComponent>
  )
}

// ── Address ─────────────────────────────────────────────────────────────
export const AddressSection = ({ application }) => {
  const current = application?.employee?.personnelInfo?.address
  const history = [...(application?.addressHistory ?? [])].sort(
    (a, b) => moment(b.from) - moment(a.from),
  )
  const since = application?.currentAddressSince
  const { needsHistory, isCovered, coveredFrom } = addressCoverage(
    since,
    history,
  )
  const chip = !since ? null : !needsHistory || isCovered ? (
    <StatusChip label={`${ADDRESS_HISTORY_YEARS} years covered`} color="success" />
  ) : (
    <StatusChip
      label={`Only covers since ${coveredFrom.format('MMM YYYY')}`}
      color="warning"
    />
  )
  return (
    <AccordComponent title="Address history" chip={chip} defaultOpen>
      <Grid item xs={12} md={8}>
        <FieldLabel>Current address</FieldLabel>
        <CopyValue value={formatAddress(current)} label="Address" />
      </Grid>
      <Grid item xs={12} md={4}>
        <FieldLabel>Lived here since</FieldLabel>
        <FieldValue
          value={since && `${formatMonth(since)} (${span(since)})`}
        />
      </Grid>
      {history.length > 0 && (
        <>
          <SubHeading>Previous addresses</SubHeading>
          {history.map((entry, index) => (
            <Grid item xs={12} key={entry._id ?? index}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                gap={{ xs: 0.25, sm: 2 }}
              >
                <FieldValue value={formatAddress(entry)} />
                <Typography variant="body2" flexShrink={0}>
                  {formatMonth(entry.from) ?? '?'} –{' '}
                  {formatMonth(entry.to) ?? '?'}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </>
      )}
    </AccordComponent>
  )
}

// ── Employment ──────────────────────────────────────────────────────────
const TimelineItem = ({ tone = 'primary', children, last }) => (
  <Stack direction="row" gap={2}>
    <Stack alignItems="center" flexShrink={0} pt={0.6}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: `${tone}.main`,
          bgcolor: tone === 'warning' ? 'transparent' : `${tone}.main`,
        }}
      />
      {!last && (
        <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.5 }} />
      )}
    </Stack>
    <Box flex={1} minWidth={0} pb={last ? 0 : 2.5}>
      {children}
    </Box>
  </Stack>
)

// Work addresses use line1/line2 rather than the personal lineOne/lineTwo
const employerAddress = (job) =>
  formatAddress({
    lineOne: job.employerAddress?.line1,
    lineTwo: job.employerAddress?.line2,
    town: job.employerAddress?.town,
    county: job.employerAddress?.county,
    postCode: job.employerAddress?.postCode,
  })

export const EmploymentSection = ({ application }) => {
  const jobs = application?.workExperience ?? []
  const gaps = employmentGaps(jobs)

  // Jobs most recent first, with each gap slotted in where it fell
  const entries = [
    ...jobs.map((job) => ({
      kind: 'job',
      job,
      sortKey: job.stillPresent
        ? Infinity
        : moment(job.endDate ?? job.startDate).valueOf() || 0,
    })),
    ...gaps.map((gap) => ({
      kind: 'gap',
      gap,
      sortKey: (gap.to ?? moment()).valueOf() - 1,
    })),
  ].sort((a, b) => b.sortKey - a.sortKey)

  const chip = application?.noEmploymentHistory ? (
    <StatusChip label="No previous employment" />
  ) : gaps.length ? (
    <StatusChip
      label={`${gaps.length} gap${gaps.length === 1 ? '' : 's'}`}
      color="warning"
    />
  ) : jobs.length ? (
    <StatusChip label="No gaps" color="success" />
  ) : null

  return (
    <AccordComponent title="Employment history" chip={chip} defaultOpen>
      <Grid item xs={12}>
        {application?.noEmploymentHistory ? (
          <Typography variant="body2">
            The applicant declared they have not been employed before
            (including voluntary work).
          </Typography>
        ) : !jobs.length ? (
          <FieldValue value={null} />
        ) : (
          entries.map((entry, index) =>
            entry.kind === 'gap' ? (
              <TimelineItem
                key={`gap-${index}`}
                tone="warning"
                last={index === entries.length - 1}
              >
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  Gap · {describeGap(entry.gap)} (
                  {span(entry.gap.from, entry.gap.to)})
                </Typography>
              </TimelineItem>
            ) : (
              <TimelineItem
                key={entry.job._id ?? index}
                tone={entry.job.stillPresent ? 'success' : 'primary'}
                last={index === entries.length - 1}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Typography fontWeight={600}>
                    {entry.job.jobTitle || 'Untitled role'}
                  </Typography>
                  {entry.job.stillPresent && (
                    <StatusChip label="Current" color="success" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.primary">
                  {entry.job.employerName || 'Unknown employer'}
                  {entry.job.employerAddress?.town &&
                    ` · ${entry.job.employerAddress.town}`}
                </Typography>
                <Typography variant="body2">
                  {formatMonth(entry.job.startDate) ?? '?'} –{' '}
                  {entry.job.stillPresent
                    ? 'present'
                    : (formatMonth(entry.job.endDate) ?? '?')}
                  {entry.job.startDate &&
                    ` · ${span(
                      entry.job.startDate,
                      entry.job.stillPresent ? null : entry.job.endDate,
                    )}`}
                </Typography>
                {entry.job.responsibilities && (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    mt={0.75}
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {entry.job.responsibilities}
                  </Typography>
                )}
                <Stack
                  direction="row"
                  columnGap={3}
                  rowGap={0.5}
                  flexWrap="wrap"
                  mt={0.75}
                >
                  {entry.job.reasonForLeaving && (
                    <Typography variant="body2">
                      <b>Left:</b> {entry.job.reasonForLeaving}
                    </Typography>
                  )}
                  {entry.job.employerPhone && (
                    <Typography variant="body2">
                      <b>Tel:</b> {entry.job.employerPhone}
                    </Typography>
                  )}
                  {entry.job.email && (
                    <Typography variant="body2">
                      <b>Email:</b> {entry.job.email}
                    </Typography>
                  )}
                  {employerAddress(entry.job) && (
                    <Typography variant="body2">
                      <b>Address:</b> {employerAddress(entry.job)}
                    </Typography>
                  )}
                </Stack>
              </TimelineItem>
            ),
          )
        )}
      </Grid>
      {(gaps.length > 0 || application?.employmentGapsExplanation) && (
        <>
          <SubHeading>Explanation of gaps</SubHeading>
          <Grid item xs={12}>
            <FieldValue value={application?.employmentGapsExplanation} />
          </Grid>
        </>
      )}
    </AccordComponent>
  )
}

// ── Supporting statement ────────────────────────────────────────────────
export const StatementSection = ({ application }) => (
  <AccordComponent title="Supporting statement">
    <Grid item xs={12}>
      <Typography
        variant="body1"
        sx={{ whiteSpace: 'pre-line', lineHeight: 1.7, maxWidth: '75ch' }}
      >
        {application?.personalStatement || '—'}
      </Typography>
    </Grid>
  </AccordComponent>
)

// ── Right to work ───────────────────────────────────────────────────────
export const RightToWorkSection = ({ application }) => {
  const rtw = application?.rightToWork ?? {}
  return (
    <AccordComponent title="Right to work">
      <Line title="Citizenship" value={rtw.citizenship} />
      <Line title="UK passport" value={yesNo(rtw.hasUKPassport)} />
      <Line title="Birth certificate" value={yesNo(rtw.hasBirthCertificate)} />
      {rtw.certificateRegistration !== undefined && (
        <Line
          title="Registration / naturalisation certificate"
          value={yesNo(rtw.certificateRegistration)}
        />
      )}
      <Line title="Documents" fullWidth>
        <DocumentList documents={rtw.documents} />
      </Line>
    </AccordComponent>
  )
}

// ── Qualifications ──────────────────────────────────────────────────────
export const QualificationsSection = ({ application }) => {
  const registration = application?.employee?.registrationNumber
  const isHCP = application?.qualifications?.isHCP
  return (
    <AccordComponent title="Qualifications">
      <Line title="Role applied for" value={application?.employee?.clinicalRole} />
      <Line title="Registered HCP" value={yesNo(isHCP)} />
      {isHCP && (
        <>
          <Line title="Registration body" value={registration?.body} />
          <Line title="Registration number">
            <CopyValue value={registration?.number} label="Registration number" />
          </Line>
          <Line title="Renewal date" value={formatDay(registration?.renewDate)} />
        </>
      )}
      <Line title="Certificates" fullWidth>
        <DocumentList documents={application?.qualifications?.documents} />
      </Line>
    </AccordComponent>
  )
}

// ── DBS ─────────────────────────────────────────────────────────────────
export const DbsSection = ({ application }) => {
  const dbs = application?.dbs ?? {}
  return (
    <AccordComponent title="DBS">
      <Line title="Certificate under 3 months old" value={yesNo(dbs.has3month)} />
      <Line title="On Update Service" value={yesNo(dbs.onUpdateService)} />
      <Line title="Certificate number">
        <CopyValue value={dbs.certificateNumber} label="Certificate number" mono />
      </Line>
      <Line title="Consents to status check" value={yesNo(dbs.consentsToStatusCheck)} />
      <Line title="Documents" fullWidth>
        <DocumentList documents={dbs.documents} />
      </Line>
    </AccordComponent>
  )
}

// ── Driving ─────────────────────────────────────────────────────────────
export const DrivingSection = ({ application }) => {
  const driving = application?.driving ?? {}
  const readyToCheck = !!(driving.licenceLastEight && driving.checkCode)
  const chip =
    driving.hasValidLicence === false ? (
      <StatusChip label="No licence" />
    ) : driving.hasValidLicence ? (
      <StatusChip
        label={readyToCheck ? 'Ready for DVLA check' : 'Check details missing'}
        color={readyToCheck ? 'success' : 'warning'}
      />
    ) : null
  return (
    <AccordComponent title="Driving" chip={chip} defaultOpen>
      <Line title="Holds a UK licence" value={yesNo(driving.hasValidLicence)} />
      {driving.hasValidLicence !== false && (
        <>
          <Line title="C1 entitlement" value={yesNo(driving.hasC1)} />
          <Line title="Blue light qualified" value={yesNo(driving.hasBlueLight)} />
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'anMuted.main',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>Licence number (last 8)</FieldLabel>
                  <CopyValue
                    value={driving.licenceLastEight}
                    label="Licence number"
                    mono
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FieldLabel>DVLA check code</FieldLabel>
                  <CopyValue value={driving.checkCode} label="Check code" mono />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    endIcon={<OpenInNewIcon />}
                    href={DVLA_CHECK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!readyToCheck}
                    fullWidth
                  >
                    Check on GOV.UK
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Check codes expire 21 days after the applicant creates
                    them. If GOV.UK rejects it, ask the applicant for a new
                    one.
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Line title="Documents" fullWidth>
            <DocumentList documents={driving.documents} />
          </Line>
        </>
      )}
    </AccordComponent>
  )
}
